import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';

import {
  workflowDecisionGateRequest,
  type WorkflowReadOnlyGraph,
  type WorkflowReadOnlyGraphDecisionGate,
  type WorkflowReadOnlyGraphDecisionGateRequest,
  type WorkflowReadOnlyGraphDecisionScopeRequest,
  type WorkflowReadOnlyGraphEdge,
  type WorkflowReadOnlyGraphNode,
  type WorkflowReadOnlyGraphSelection,
  type WorkflowReadOnlyGraphSelectionRequest,
} from './workflow-read-only-graph.models';
import {
  type WorkflowGraphViewport,
  type WorkflowGraphLayoutProfile,
  type WorkflowGraphCameraState,
  validWorkflowGraphCameraState,
  WorkflowReadOnlyGraphVisualizationService,
} from './workflow-read-only-graph-visualization.service';
import {readWorkflowMapPositions,saveWorkflowMapPositions,validPoint,type WorkflowMapPoint,type WorkflowMapPositions} from './workflow-map-layout';

type WorkflowGraphSelectableElement =
  | WorkflowReadOnlyGraphNode
  | WorkflowReadOnlyGraphEdge;
type WorkflowGraphDiagramMode = 'overview' | 'responsibility';
export type WorkflowGraphSourceContext = 'working-draft' | 'published-version';

export interface WorkflowGraphPresentationState {
  readonly version: 1;
  readonly contextKey: string;
  readonly camera: WorkflowGraphCameraState;
  readonly scrollLeft: number;
  readonly scrollTop: number;
}

interface WorkflowGraphRenderRequest {
  readonly graph: WorkflowReadOnlyGraph;
  readonly profile: WorkflowGraphLayoutProfile;
  readonly structuralKey: string;
}

interface WorkflowGraphContextualAction {
  readonly kind: 'node' | 'edge';
  readonly label: 'Open in Steps' | 'Open in Routing';
  readonly sourceId: string;
}

interface WorkflowGraphContainerSize {
  readonly width: number;
  readonly height: number;
}

// Deliberately larger than a desktop scrollbar and the observed 10px inspector
// reflow. Changes accumulate against the last fitted size, so a gradual browser
// resize still crosses the threshold and re-fits the whole graph.
const CAMERA_REFIT_RESIZE_THRESHOLD_PX = 24;

@Component({
  selector: 'phms-workflow-read-only-graph',
  standalone: true,
  imports: [UiButtonDirective, UiControlDirective, CommonModule],
  providers: [WorkflowReadOnlyGraphVisualizationService],
  templateUrl: './workflow-read-only-graph.component.html',
  styleUrl: './workflow-read-only-graph.component.scss',
})
export class WorkflowReadOnlyGraphComponent implements OnDestroy {
  private readonly visualization = inject(
    WorkflowReadOnlyGraphVisualizationService,
  );
  private readonly document = inject(DOCUMENT);
  private readonly graphState = signal<WorkflowReadOnlyGraph | null>(null);
  private readonly graphContainerRef = signal<ElementRef<HTMLElement> | null>(
    null,
  );
  private readonly renderRequest = signal<WorkflowGraphRenderRequest | null>(
    null,
  );
  private readonly retryVersion = signal(0);
  private readonly containerMeasurementVersion = signal(0);
  private renderSequence = 0;
  private controlledSelectionId: string | null = null;
  private contextualInspectorEnabled = false;
  private viewport?: WorkflowGraphViewport;
  private resizeObserver?: ResizeObserver;
  private themeClassObserver?: MutationObserver;
  private readonly onThemeStylesheetLoad = (event: Event): void => {
    if ((event.target as HTMLElement | null)?.id === 'theme-link') {
      this.applyCurrentTheme();
    }
  };
  private observedDarkMode = false;
  private lastCameraFitContainerSize: WorkflowGraphContainerSize | null = null;
  private pendingPresentationRestore: WorkflowGraphPresentationState | null = null;
  private presentationRestoreFrame: number | null = null;
  private destroyed = false;
  private readonly layoutKeyState=signal<string|null>(null);
  private positions:WorkflowMapPositions={};
  private positionsLoaded=false;
  readonly layoutMessage=signal('');
  readonly inspectorWidth=signal(320);
  readonly inspectorCollapsed=signal(false);
  private inspectorPreferencesLoaded=false;
  private inspectorDrag:{pointerId:number;startX:number;width:number;element:HTMLElement}|null=null;
  private loadInspectorPreferences():void {
    if(this.inspectorPreferencesLoaded)return;this.inspectorPreferencesLoaded=true;
    try{const value=JSON.parse(this.storage()?.getItem('phms.workflow-designer.inspector.v1')??'null');
      if(value?.version===1){if(Number.isFinite(value.width))this.inspectorWidth.set(Math.max(260,Math.min(640,value.width)));
        this.inspectorCollapsed.set(value.collapsed===true);}}catch{/* Keep usable defaults when browser preferences are unavailable. */}
  }
  private saveInspectorPreferences():void {
    try{this.storage()?.setItem('phms.workflow-designer.inspector.v1',JSON.stringify({version:1,width:this.inspectorWidth(),collapsed:this.inspectorCollapsed()}));}catch{/* Resizing still works for this view. */}
  }
  toggleInspector():void {this.inspectorCollapsed.update(v=>!v);this.saveInspectorPreferences();}
  startInspectorResize(event:PointerEvent):void {
    if(event.button!==0||this.inspectorCollapsed())return;
    const element=event.currentTarget as HTMLElement;
    if(this.document.defaultView?.getComputedStyle(element.parentElement!).display!=='grid')return;
    event.preventDefault();element.setPointerCapture(event.pointerId);
    this.inspectorDrag={pointerId:event.pointerId,startX:event.clientX,width:this.inspectorWidth(),element};
  }
  resizeInspector(event:PointerEvent):void {
    const drag=this.inspectorDrag;if(!drag||drag.pointerId!==event.pointerId)return;
    const maximum=Math.min(640,Math.max(260,(drag.element.parentElement?.clientWidth??1000)-360));
    this.inspectorWidth.set(Math.max(260,Math.min(maximum,drag.width+drag.startX-event.clientX)));
  }
  endInspectorResize(event:PointerEvent):void {
    const drag=this.inspectorDrag;if(!drag||drag.pointerId!==event.pointerId)return;
    this.inspectorDrag=null;if(drag.element.hasPointerCapture(event.pointerId))drag.element.releasePointerCapture(event.pointerId);this.saveInspectorPreferences();
  }
  onInspectorResizeKeydown(event:KeyboardEvent):void {
    const change=event.key==='ArrowLeft'?20:event.key==='ArrowRight'?-20:0;
    if(!change&&event.key!=='Home'&&event.key!=='End')return;
    event.preventDefault();this.inspectorWidth.set(event.key==='Home'?260:event.key==='End'?640:Math.max(260,Math.min(640,this.inspectorWidth()+change)));this.saveInspectorPreferences();
  }
  @Input() set layoutStorageKey(value:string|null){
    if(value===this.layoutKeyState())return;
    this.cancelPresentationRestore();
    this.layoutKeyState.set(value);this.positions={};this.positionsLoaded=false;this.queueRender(this.graphState());
  }
  get layoutStorageKey(): string | null { return this.layoutKeyState(); }
  get canArrangeNodes():boolean{return this.layoutKeyState()!==null&&this.isOverviewMode()&&this.sourceContext!=='published-version';}
  private storage():Storage|null {try{return this.document.defaultView?.localStorage??null;}catch{return null;}}

  @ViewChild('graphContainer')
  set graphContainer(value: ElementRef<HTMLElement> | undefined) {
    this.graphContainerRef.set(value ?? null);
    this.connectResizeObserver(value?.nativeElement);
  }

  @ViewChild('renderError')
  private renderError?: ElementRef<HTMLElement>;

  @ViewChild('elementFilterInput')
  private elementFilterInput?: ElementRef<HTMLInputElement>;

  @ViewChild('elementFinderToggle')
  private elementFinderToggle?: ElementRef<HTMLButtonElement>;

  @ViewChild('diagramModeToggle')
  private diagramModeToggle?: ElementRef<HTMLButtonElement>;

  private restoreDiagramModeToggleFocusAfterRender = false;

  @Input({ required: true })
  set graph(value: WorkflowReadOnlyGraph | null) {
    this.graphState.set(value);
    this.queueRender(value);
    if (this.pendingPresentationRestore?.contextKey !== this.presentationContextKey()) this.cancelPresentationRestore();

    const selectedId = this.selectionControlled
      ? this.controlledSelectionId
      : this.selectedElementId();
    this.commitSelection(selectedId);
  }

  get graph(): WorkflowReadOnlyGraph | null {
    return this.graphState();
  }

  capturePresentationState(): WorkflowGraphPresentationState | null {
    const container = this.graphContainerRef()?.nativeElement;
    if (this.destroyed || !container?.isConnected || container.closest('[hidden], [inert]') || !hasRenderableSize(container)
      || this.isBusy() || this.lastRenderedStructuralKey() !== this.currentStructuralKey()) return null;
    const camera = this.visualization.captureCamera(this.viewport), scroll = container.parentElement;
    const contextKey = this.presentationContextKey();
    return camera && contextKey ? { version: 1, contextKey, camera,
      scrollLeft: scroll?.scrollLeft ?? 0, scrollTop: scroll?.scrollTop ?? 0 } : null;
  }

  /** True means accepted for this exact graph; a hidden map is restored when visible. */
  restorePresentationState(state: WorkflowGraphPresentationState): boolean {
    if (this.destroyed || !state || state.version !== 1 || state.contextKey !== this.presentationContextKey()
      || !validWorkflowGraphCameraState(state.camera) || !validPresentationScroll(state.scrollLeft)
      || !validPresentationScroll(state.scrollTop)) return false;
    this.pendingPresentationRestore = { ...state, camera: { zoom: state.camera.zoom, pan: { ...state.camera.pan } } };
    this.schedulePresentationRestore();
    return true;
  }

  private presentationContextKey(): string | null {
    const graph = this.graphState();
    return graph ? JSON.stringify([this.sourceContext, this.layoutKeyState(), this.renderProfile(), graph]) : null;
  }

  private cancelPresentationRestore(): void {
    const view = this.document.defaultView;
    if (this.presentationRestoreFrame !== null) view?.cancelAnimationFrame(this.presentationRestoreFrame);
    this.presentationRestoreFrame = null;
    this.pendingPresentationRestore = null;
  }

  private schedulePresentationRestore(): void {
    if (!this.pendingPresentationRestore || this.presentationRestoreFrame !== null || this.destroyed) return;
    const view = this.document.defaultView;
    if (!view) return;
    if (typeof view.requestAnimationFrame !== 'function') { this.applyPendingPresentationRestore(); return; }
    this.presentationRestoreFrame = view.requestAnimationFrame(() => {
      this.presentationRestoreFrame = null;
      this.applyPendingPresentationRestore();
    });
  }

  private applyPendingPresentationRestore(): boolean {
    const state = this.pendingPresentationRestore;
    if (!state) return false;
    if (this.destroyed || state.contextKey !== this.presentationContextKey()) { this.cancelPresentationRestore(); return false; }
    const container = this.graphContainerRef()?.nativeElement;
    if (!container || container.closest('[hidden], [inert]') || !hasRenderableSize(container)
      || this.isBusy() || this.lastRenderedStructuralKey() !== this.currentStructuralKey()) return false;
    if (!this.visualization.restoreCamera(this.viewport, state.camera)) { this.cancelPresentationRestore(); return false; }
    const scroll = container.parentElement;
    if (scroll) { scroll.scrollLeft = state.scrollLeft; scroll.scrollTop = state.scrollTop; }
    this.recordCurrentContainerAsFitted(container);
    this.cancelPresentationRestore();
    return true;
  }

  @Input() visualAriaLabel = 'Read-only workflow graph';
  @Input() selectionControlled = false;
  @Input() sourceContext: WorkflowGraphSourceContext = 'working-draft';
  @Input() inspectorStandalone = false;

  @Input()
  set contextualInspector(value: boolean) {
    this.contextualInspectorEnabled = value;
    if(value)this.loadInspectorPreferences();
    if (!value) {
      this.elementFinderOpen.set(false);
    }
  }

  get contextualInspector(): boolean {
    return this.contextualInspectorEnabled;
  }

  @Input()
  set selectedRendererElementId(value: string | null) {
    this.controlledSelectionId = value;
    this.commitSelection(value);
  }

  @Output() readonly editNodeRequested = new EventEmitter<string>();
  @Output() readonly editEdgeRequested = new EventEmitter<string>();
  @Output() readonly selectionRequested =
    new EventEmitter<WorkflowReadOnlyGraphSelectionRequest>();
  @Output() readonly decisionGateRequested =
    new EventEmitter<WorkflowReadOnlyGraphDecisionGateRequest>();
  @Output() readonly decisionScopeRequested =
    new EventEmitter<WorkflowReadOnlyGraphDecisionScopeRequest>();

  readonly diagramMode = signal<WorkflowGraphDiagramMode>('overview');
  readonly isOverviewMode = computed(() => this.diagramMode() === 'overview');
  readonly renderProfile = computed<WorkflowGraphLayoutProfile>(() =>
    this.isOverviewMode() ? 'overview' : 'routed',
  );
  readonly isRendering = signal(false);
  readonly isWaitingForViewport = signal(false);
  readonly isBusy = computed(
    () => this.isRendering() || this.isWaitingForViewport(),
  );
  readonly renderErrorMessage = signal<string | null>(null);
  readonly lastRenderedStructuralKey = signal<string | null>(null);
  readonly hasRenderedVisual = signal(false);
  readonly selectedElementId = signal<string | null>(null);
  readonly elementFilter = signal('');
  readonly elementFinderOpen = signal(false);
  readonly tableView = signal(false);
  readonly selectedElement = computed(() =>
    findElement(this.graphState(), this.selectedElementId()),
  );
  readonly selectionAnnouncement = computed(() => {
    const item = this.selectedElement();
    const gateSummary = item ? decisionGateVisibleSummary(item) : '';
    return item
      ? `${item.detail.subtitle} ${item.detail.title} selected.${gateSummary ? ` ${gateSummary}` : ''}`
      : 'No workflow element selected.';
  });
  readonly selectableItems = computed<WorkflowGraphSelectableElement[]>(() => {
    const graph = this.graphState();
    return graph ? [...graph.nodes, ...graph.edges] : [];
  });
  readonly filteredSelectableItems = computed(() => {
    const query = this.elementFilter().trim().toLocaleLowerCase();
    if (!query) {
      return this.selectableItems();
    }

    return this.selectableItems().filter((item) =>
      searchableElementText(item).includes(query),
    );
  });
  readonly selectionGroups = computed(() => [
    ...new Set(this.filteredSelectableItems().map((item) => item.groupLabel)),
  ]);
  readonly filterResultLabel = computed(() => {
    const visibleCount =
      this.filteredSelectableItems().length +
      this.visibleStepDecisionGateCount() +
      this.workflowDecisionGateFinderItems().length;
    const totalCount =
      this.selectableItems().length +
      this.totalStepDecisionGateCount() +
      this.workflowDecisionGates().length;
    return this.elementFilter().trim()
      ? `${visibleCount} of ${totalCount} items`
      : `${totalCount} ${totalCount === 1 ? 'item' : 'items'}`;
  });
  readonly workflowDecisionGates = computed(
    () => this.graphState()?.workflowDecisionGates ?? [],
  );
  readonly workflowDecisionGateFinderItems = computed(() => {
    const query = this.elementFilter().trim().toLocaleLowerCase();
    return query
      ? this.workflowDecisionGates().filter((gate) =>
          searchableDecisionGateText(gate).includes(query),
        )
      : this.workflowDecisionGates();
  });
  readonly totalStepDecisionGateCount = computed(
    () =>
      this.graphState()?.nodes.reduce(
        (total, node) => total + (node.decisionGates?.length ?? 0),
        0,
      ) ?? 0,
  );
  readonly visibleStepDecisionGateCount = computed(() =>
    this.filteredSelectableItems().reduce(
      (total, item) => total + this.decisionGatesForItem(item).length,
      0,
    ),
  );
  readonly hasFinderResults = computed(
    () =>
      this.filteredSelectableItems().length > 0 ||
      this.visibleStepDecisionGateCount() > 0 ||
      this.workflowDecisionGateFinderItems().length > 0,
  );
  readonly workflowDecisionGateCount = computed(
    () =>
      this.graphState()?.workflowDecisionGates?.length ??
      this.graphState()?.workflowDecisionGateCount ??
      0,
  );
  readonly hasWorkflowDecisionRequirements = computed(
    () => this.workflowDecisionGateCount() > 0,
  );
  readonly workflowDecisionGateInvalidCount = computed(
    () => this.workflowDecisionGates().filter((gate) => gate.isInvalid).length,
  );
  readonly stepDecisionGateInvalidCount = computed(
    () =>
      this.graphState()?.nodes.reduce(
        (total, node) =>
          total +
          (node.decisionGates ?? []).filter((gate) => gate.isInvalid).length,
        0,
      ) ?? 0,
  );
  readonly hasProcessSequence = computed(
    () =>
      this.graphState()?.nodes.some(
        (node) =>
          node.kind === 'human-step' && (node.processOrder ?? null) !== null,
      ) ?? false,
  );
  readonly selectedContextualAction =
    computed<WorkflowGraphContextualAction | null>(() => {
      const graph = this.graphState();
      const selectedId = this.selectedElementId();
      if (!graph || !selectedId) {
        return null;
      }

      const node = graph.nodes.find((candidate) => candidate.id === selectedId);
      if (node) {
        if(this.canArrangeNodes)return null;
        return hasUnambiguousSourceId(node, graph.nodes)
          ? { kind: 'node', label: 'Open in Steps', sourceId: node.sourceId }
          : null;
      }

      const edge = graph.edges.find((candidate) => candidate.id === selectedId);
      return edge && hasUnambiguousSourceId(edge, graph.edges)
        ? { kind: 'edge', label: 'Open in Routing', sourceId: edge.sourceId }
        : null;
    });
  readonly currentStructuralKey = computed(() => {
    const graph = this.graphState();
    return graph ? JSON.stringify([buildStructuralKey(graph, this.renderProfile()),this.layoutKeyState()]) : null;
  });
  readonly isVisualStale = computed(
    () =>
      this.hasRenderedVisual() &&
      this.lastRenderedStructuralKey() !== this.currentStructuralKey(),
  );
  readonly canControlVisual = computed(
    () =>
      this.hasRenderedVisual() &&
      !this.isBusy() &&
      !this.isVisualStale() &&
      !this.renderErrorMessage(),
  );
  readonly canvasAriaLabel = computed(() => {
    const graph = this.graphState();
    const hasDecisionGates = Boolean(
      graph &&
      (graph.nodes.some((node) => (node.decisionGates?.length ?? 0) > 0) ||
        this.workflowDecisionGateCount() > 0),
    );
    const modeDescription = this.isOverviewMode()
      ? ' Compact workflow view shows the authored path and decision requirement summaries beside the stages. Decision summaries describe status requirements; they are not workflow routes.' +
        (this.hasWorkflowDecisionRequirements()
          ? ' Workflow-wide requirements are available in the strip above the canvas.'
          : '') +
        ' Use Show responsibility view to see saved decision queue and role guidance.'
      : (hasDecisionGates
          ? ' Responsibility view shows decision requirements as amber diamonds in intended-guidance lanes. Dashed associations show which workflow stage a requirement affects; they are not workflow routes.'
          : ' Responsibility view shows workflow stages and any saved decision guidance in separate lanes.') +
        ' Workflow stages remain in their own lane because this definition does not store a step owner.';
    return graph
      ? `${this.visualAriaLabel}. ${graph.description}` +
          modeDescription +
          ' Decision queue and role values are saved guidance only; they do not assign work or grant access.' +
          (this.hasProcessSequence()
            ? ' Numbered human stages show the authored path sequence only when that path is unambiguous; these numbers are not display order.'
            : '')
      : this.visualAriaLabel;
  });

  sourceEmptyMessage(): string {
    return this.sourceContext === 'published-version'
      ? 'This immutable definition document does not contain any graph nodes to inspect.'
      : 'This working document does not contain any graph nodes to preview.';
  }

  sourcePreparingMessage(): string {
    return this.sourceContext === 'published-version'
      ? 'Preparing the graph viewport. The immutable definition elements are ready for inspection.'
      : 'Preparing the graph viewport. The structured graph items are ready for inspection.';
  }

  sourceUpdatingMessage(): string {
    return this.sourceContext === 'published-version'
      ? 'Updating the graph from the selected immutable definition. The retained visual is stale until rendering completes.'
      : 'Updating the graph from the latest applied working document. The retained visual is stale until rendering completes.';
  }

  sourceRenderingMessage(): string {
    return this.sourceContext === 'published-version'
      ? 'Rendering the immutable workflow graph.'
      : 'Rendering the working-draft graph.';
  }

  contextualInspectorAriaLabel(): string {
    return this.sourceContext === 'published-version'
      ? 'Immutable workflow inspector'
      : 'Workflow editor inspector';
  }

  emptyInspectorMessage(): string {
    return this.sourceContext === 'published-version'
      ? 'Select an element to inspect its saved identity and immutable definition metadata.'
      : 'Select an element to inspect its stable identity and draft metadata. The visual canvas supports navigation and inspection; edits open in the structured editors.';
  }

  constructor() {
    this.connectThemeClassObserver();

    effect(() => {
      const request = this.renderRequest();
      const container = this.graphContainerRef()?.nativeElement;
      this.retryVersion();
      this.containerMeasurementVersion();

      if (!request || request.graph.nodes.length === 0) {
        this.clearVisual();
        return;
      }

      if (!container) {
        return;
      }

      const retainedVisualMatches = untracked(
        () =>
          this.hasRenderedVisual() &&
          request.structuralKey === this.lastRenderedStructuralKey(),
      );
      if (retainedVisualMatches) {
        this.renderSequence += 1;
        this.isRendering.set(false);
        this.isWaitingForViewport.set(false);
        this.renderErrorMessage.set(null);
        this.visualization.select(this.viewport, this.selectedElementId());
        this.restoreDiagramModeToggleFocus();
        return;
      }

      if (!hasRenderableSize(container)) {
        this.isRendering.set(false);
        this.isWaitingForViewport.set(true);
        return;
      }

      this.isWaitingForViewport.set(false);
      void this.renderGraph(container, request);
    });
  }

  itemsForGroup(groupLabel: string): WorkflowGraphSelectableElement[] {
    return this.filteredSelectableItems().filter(
      (item) => item.groupLabel === groupLabel,
    );
  }

  selectItem(
    item: WorkflowGraphSelectableElement,
    moveFocusToInspector = false,
  ): void {
    this.requestSelection(item, 'picker', moveFocusToInspector);
  }

  selectRouteFromTable(rendererId: string): void {
    const item = this.selectableItems().find(
      (candidate) => candidate.id === rendererId,
    );
    if (item) this.selectItem(item, true);
  }

  setElementFilter(event: Event): void {
    this.elementFilter.set((event.target as HTMLInputElement).value);
  }

  clearElementFilter(): void {
    this.elementFilter.set('');
  }

  toggleElementFinder(): void {
    if (this.elementFinderOpen()) {
      this.closeElementFinder();
      return;
    }

    this.elementFinderOpen.set(true);
    setTimeout(() => {
      this.visualization.resize(this.viewport);
      this.visualization.fit(this.viewport);
      this.recordCurrentContainerAsFitted();
      this.elementFilterInput?.nativeElement.focus();
    });
  }

  closeElementFinder(moveFocusToToggle = true): void {
    if (!this.elementFinderOpen()) {
      return;
    }

    this.elementFinderOpen.set(false);
    setTimeout(() => {
      this.visualization.resize(this.viewport);
      this.visualization.fit(this.viewport);
      this.recordCurrentContainerAsFitted();
      if (moveFocusToToggle) {
        this.elementFinderToggle?.nativeElement.focus();
      }
    });
  }

  onElementFinderKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closeElementFinder();
  }

  requestContextualEdit(action: WorkflowGraphContextualAction): void {
    if (action.kind === 'node') {
      this.editNodeRequested.emit(action.sourceId);
      return;
    }

    this.editEdgeRequested.emit(action.sourceId);
  }

  retry(): void {
    if (!this.graphState() || this.isRendering()) {
      return;
    }

    this.retryVersion.update((version) => version + 1);
  }

  toggleDiagramMode(): void {
    if (this.isBusy()) {
      return;
    }

    const toggle = this.diagramModeToggle?.nativeElement;
    this.restoreDiagramModeToggleFocusAfterRender = Boolean(
      toggle && toggle.ownerDocument.activeElement === toggle,
    );
    this.diagramMode.update((mode) =>
      mode === 'overview' ? 'responsibility' : 'overview',
    );
    this.queueRender(this.graphState());
  }

  requestWorkflowDecisionScope(): void {
    this.requestDecisionScope(
      {
        ownerSourceNodeId: null,
        ownerSourceNodeIndex: null,
      },
      'picker',
    );
  }

  zoomIn(): void {
    this.visualization.zoomBy(this.viewport, 1.18);
  }

  zoomOut(): void {
    this.visualization.zoomBy(this.viewport, 0.84);
  }

  fitGraph(): void {
    this.visualization.fit(this.viewport);
    this.recordCurrentContainerAsFitted();
  }

  autoArrange():void {
    if(!this.canArrangeNodes||this.isBusy())return;
    this.positions={};this.positionsLoaded=true;
    const saved=saveWorkflowMapPositions(this.storage(),this.layoutKeyState(),this.positions);
    this.layoutMessage.set(saved?'Default arrangement restored.':'Default arrangement restored for this view; browser storage is unavailable.');
    this.lastRenderedStructuralKey.set(null);this.retryVersion.update(v=>v+1);
  }
  onMapKeydown(event:KeyboardEvent):void {
    if(!this.canArrangeNodes||this.isBusy()||event.altKey||event.ctrlKey||event.metaKey)return;
    const shifts:Record<string,[number,number]>={ArrowLeft:[-1,0],ArrowRight:[1,0],ArrowUp:[0,-1],ArrowDown:[0,1]};
    const direction=shifts[event.key],id=this.selectedElementId();if(!direction||!id||!this.graphState()?.nodes.some(n=>n.id===id))return;
    event.preventDefault();event.stopPropagation();const step=event.shiftKey?40:10;
    const point=this.visualization.moveNode(this.viewport,id,direction[0]*step,direction[1]*step);
    if(point)this.rememberPosition(id,point);
  }
  private rememberPosition(id:string,point:WorkflowMapPoint):void {
    if(!this.canArrangeNodes||!validPoint(point)||!this.graphState()?.nodes.some(n=>n.id===id&&n.sourceIndex!==null))return;
    this.positions={...this.positions,[id]:point};
    const saved=saveWorkflowMapPositions(this.storage(),this.layoutKeyState(),this.positions);
    this.layoutMessage.set(saved?'Node position saved.':'Position updated for this view; browser storage is unavailable.');
  }

  trackElement(_index: number, item: WorkflowGraphSelectableElement): string {
    return item.id;
  }

  trackDecisionGate(
    _index: number,
    gate: WorkflowReadOnlyGraphDecisionGate,
  ): string {
    return gate.id;
  }

  inspectAriaLabel(
    group: string,
    item: WorkflowGraphSelectableElement,
  ): string {
    const gateSummary = decisionGateVisibleSummary(item);
    return `Inspect ${group} ${item.label}${gateSummary ? `. ${gateSummary}` : ''}`;
  }

  decisionGateSummary(item: WorkflowGraphSelectableElement): string {
    return decisionGateVisibleSummary(item);
  }

  decisionGateFullDescription(item: WorkflowGraphSelectableElement): string {
    return decisionGateAccessibleSummary(item);
  }

  decisionGateDescriptionId(
    item: WorkflowGraphSelectableElement,
  ): string | null {
    return decisionGateCount(item) > 0
      ? `workflow-graph-gates-${encodeURIComponent(item.id)}`
      : null;
  }

  decisionGatesForItem(
    item: WorkflowGraphSelectableElement,
  ): readonly WorkflowReadOnlyGraphDecisionGate[] {
    const gates = 'decisionGates' in item ? (item.decisionGates ?? []) : [];
    const query = this.elementFilter().trim().toLocaleLowerCase();
    if (!query || searchableElementCoreText(item).includes(query)) {
      return gates;
    }
    return gates.filter((gate) =>
      searchableDecisionGateText(gate).includes(query),
    );
  }

  decisionGateButtonText(gate: WorkflowReadOnlyGraphDecisionGate): string {
    return (
      (this.isDecisionGateOwnerAuthorable(gate)
        ? gate.title
        : `Inspect ${gate.title} repair`) +
      (gate.authoredOriginCount > 1
        ? ` · ${gate.authoredOriginCount} saved`
        : '') +
      (gate.isLegacyOnly ? ' · legacy' : '')
    );
  }

  decisionGateButtonAriaLabel(gate: WorkflowReadOnlyGraphDecisionGate): string {
    const grouped =
      gate.authoredOriginCount > 1
        ? ` This represents ${gate.authoredOriginCount} equivalent saved requirements and opens the first.`
        : '';
    return this.isDecisionGateOwnerAuthorable(gate)
      ? `Open ${gate.title} at ${gate.ownerLabel}.${grouped}`
      : `Inspect ${gate.ownerLabel} repair before opening ${gate.title}.${grouped}`;
  }

  decisionGateButtonDescriptionId(
    gate: WorkflowReadOnlyGraphDecisionGate,
  ): string {
    return `workflow-graph-gate-${encodeURIComponent(gate.id)}`;
  }

  decisionGateButtonDescription(
    gate: WorkflowReadOnlyGraphDecisionGate,
  ): string {
    const grouping =
      gate.authoredOriginCount > 1
        ? ` This diagram groups ${gate.authoredOriginCount} semantically identical saved requirements. Opening it selects the first saved requirement in document order.`
        : '';
    const unaddressable =
      gate.authoredOrigins[0]?.decisionIndex === null
        ? ' This malformed collection cannot be opened as an individual rule. The editor will show its containing evidence and will not start a new rule.'
        : '';
    return gate.accessibleDescription + grouping + unaddressable;
  }

  activateDecisionGate(gate: WorkflowReadOnlyGraphDecisionGate): void {
    const request = workflowDecisionGateRequest(gate);
    if (request) {
      this.requestDecisionGate(request, 'picker');
    }
  }

  private isDecisionGateOwnerAuthorable(
    gate: WorkflowReadOnlyGraphDecisionGate,
  ): boolean {
    if (gate.ownerSourceIndex === null) {
      return true;
    }
    const graph = this.graphState();
    const owner =
      graph?.nodes.find(
        (node) =>
          node.sourceIndex === gate.ownerSourceIndex &&
          node.sourceId === gate.ownerSourceId,
      ) ?? null;
    return Boolean(
      graph && owner && hasUnambiguousSourceId(owner, graph.nodes),
    );
  }

  private requestDecisionGate(
    received: WorkflowReadOnlyGraphDecisionGateRequest,
    origin: WorkflowReadOnlyGraphSelectionRequest['origin'],
  ): void {
    const graph = this.graphState();
    const gate = graph ? findDecisionGate(graph, received.gateId) : null;
    const canonicalRequest = gate ? workflowDecisionGateRequest(gate) : null;
    if (!graph || !gate || !canonicalRequest) {
      return;
    }

    if (canonicalRequest.ownerSourceNodeId === null) {
      this.decisionGateRequested.emit(canonicalRequest);
      return;
    }

    const owner =
      graph.nodes.find(
        (node) =>
          node.sourceIndex === canonicalRequest.ownerSourceNodeIndex &&
          node.sourceId === canonicalRequest.ownerSourceNodeId,
      ) ?? null;
    if (!owner || !hasUnambiguousSourceId(owner, graph.nodes)) {
      if (owner) {
        this.requestSelection(owner, origin, true);
      }
      return;
    }

    this.decisionGateRequested.emit(canonicalRequest);
  }

  private requestDecisionScope(
    received: WorkflowReadOnlyGraphDecisionScopeRequest,
    origin: WorkflowReadOnlyGraphSelectionRequest['origin'],
  ): void {
    const graph = this.graphState();
    if (!graph) {
      return;
    }

    if (
      received.ownerSourceNodeId === null &&
      received.ownerSourceNodeIndex === null
    ) {
      this.decisionScopeRequested.emit(received);
      return;
    }

    if (
      received.ownerSourceNodeId === null ||
      received.ownerSourceNodeIndex === null
    ) {
      return;
    }

    const owner =
      graph.nodes.find(
        (node) =>
          node.sourceIndex === received.ownerSourceNodeIndex &&
          node.sourceId === received.ownerSourceNodeId,
      ) ?? null;
    if (!owner || !hasUnambiguousSourceId(owner, graph.nodes)) {
      if (owner) {
        this.requestSelection(owner, origin, true);
      }
      return;
    }

    this.decisionScopeRequested.emit({
      ownerSourceNodeId: owner.sourceId,
      ownerSourceNodeIndex: owner.sourceIndex,
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelPresentationRestore();
    this.renderSequence += 1;
    this.restoreDiagramModeToggleFocusAfterRender = false;
    this.resizeObserver?.disconnect();
    this.themeClassObserver?.disconnect();
    this.document.removeEventListener('load', this.onThemeStylesheetLoad, true);
    this.visualization.cancelPendingFrames();
    this.visualization.destroy(this.viewport);
    this.viewport = undefined;
  }

  private async renderGraph(
    container: HTMLElement,
    request: WorkflowGraphRenderRequest,
  ): Promise<void> {
    const sequence = ++this.renderSequence;
    let renderedSuccessfully = false;
    this.isRendering.set(true);
    this.renderErrorMessage.set(null);

    try {
      const layout = await this.visualization.layout(
        request.graph,
        request.profile,
      );
      if (sequence !== this.renderSequence) {
        return;
      }

      const nextViewport = await this.visualization.render(
        container,
        request.graph,
        layout,
        {
          positions: this.canArrangeNodes ? this.savedPositions(request.graph) : undefined,
          move: this.canArrangeNodes ? (id,point)=>{if(request.structuralKey===this.currentStructuralKey())this.rememberPosition(id,point);} : undefined,
          select: (elementId) =>
            this.selectFromVisual(elementId, request.structuralKey),
          clear: () => this.clearSelectionFromVisual(request.structuralKey),
          decisionGate: (gateRequest) => {
            if(this.canArrangeNodes)return;
            if (request.structuralKey === this.currentStructuralKey()) {
              this.requestDecisionGate(gateRequest, 'visual');
            }
          },
          decisionScope: (scopeRequest) => {
            if(this.canArrangeNodes)return;
            if (request.structuralKey === this.currentStructuralKey()) {
              this.requestDecisionScope(scopeRequest, 'visual');
            }
          },
        },
        request.profile,
      );
      if (sequence !== this.renderSequence) {
        this.visualization.destroy(nextViewport);
        return;
      }

      const previousViewport = this.viewport;
      this.viewport = nextViewport;
      this.visualization.destroy(previousViewport);
      this.applyCurrentTheme();
      this.lastRenderedStructuralKey.set(request.structuralKey);
      this.hasRenderedVisual.set(true);
      this.visualization.select(this.viewport, this.selectedElementId());
      this.recordCurrentContainerAsFitted(container);
      renderedSuccessfully = true;
    } catch {
      if (sequence === this.renderSequence) {
        this.restoreDiagramModeToggleFocusAfterRender = false;
        this.renderErrorMessage.set(
          this.sourceContext === 'published-version'
            ? 'The immutable workflow graph could not be rendered. Use the Elements list to inspect the version definition document, then retry.'
            : 'The current working-draft graph could not be rendered. Use the Elements list to inspect the current document, then retry.',
        );
        this.focusRenderError();
      }
    } finally {
      if (sequence === this.renderSequence) {
        this.isRendering.set(false);
        if (renderedSuccessfully) {
          this.schedulePresentationRestore();
          this.restoreDiagramModeToggleFocus();
        }
      }
    }
  }

  private selectFromVisual(
    elementId: string,
    renderedStructuralKey: string,
  ): void {
    if (renderedStructuralKey !== this.currentStructuralKey()) {
      return;
    }

    const element = findElement(this.graphState(), elementId);
    if (element) {
      this.requestSelection(element, 'visual', false);
    }
  }

  private clearSelectionFromVisual(renderedStructuralKey: string): void {
    if (renderedStructuralKey !== this.currentStructuralKey()) {
      return;
    }

    this.requestSelection(null, 'visual', false);
  }

  private requestSelection(
    element: WorkflowGraphSelectableElement | null,
    origin: WorkflowReadOnlyGraphSelectionRequest['origin'],
    moveFocusToInspector: boolean,
  ): void {
    if (this.canArrangeNodes && element && origin === 'visual')
      this.graphContainerRef()?.nativeElement.focus({ preventScroll: true });
    const selection = element ? this.toSelection(element) : null;
    if (!this.selectionControlled) {
      this.commitSelection(selection?.rendererId ?? null);
    }
    this.selectionRequested.emit({ selection, origin, moveFocusToInspector });
  }

  private commitSelection(rendererId: string | null): void {
    const acceptedId =
      rendererId && findElement(this.graphState(), rendererId)
        ? rendererId
        : null;
    if (this.contextualInspector && acceptedId && acceptedId !== this.selectedElementId()) this.inspectorCollapsed.set(false);
    this.selectedElementId.set(acceptedId);
    if (this.lastRenderedStructuralKey() === this.currentStructuralKey()) {
      this.visualization.select(this.viewport, acceptedId);
    }
  }

  private toSelection(
    element: WorkflowGraphSelectableElement,
  ): WorkflowReadOnlyGraphSelection {
    const graph = this.graphState();
    const isNode = Boolean(graph?.nodes.some((node) => node.id === element.id));
    const candidates = isNode ? (graph?.nodes ?? []) : (graph?.edges ?? []);
    return {
      kind: isNode ? 'node' : 'edge',
      rendererId: element.id,
      sourceId: element.sourceId,
      sourceIndex: element.sourceIndex,
      isValid: !element.isInvalid,
      isAuthorable:
        element.sourceIndex !== null &&
        hasUnambiguousSourceId(element, candidates),
    };
  }

  private clearVisual(): void {
    this.renderSequence += 1;
    this.restoreDiagramModeToggleFocusAfterRender = false;
    this.isRendering.set(false);
    this.isWaitingForViewport.set(false);
    this.renderErrorMessage.set(null);
    this.lastRenderedStructuralKey.set(null);
    this.hasRenderedVisual.set(false);
    this.selectedElementId.set(null);
    this.visualization.cancelPendingFrames();
    this.visualization.destroy(this.viewport);
    this.viewport = undefined;
  }

  private restoreDiagramModeToggleFocus(): void {
    if (!this.restoreDiagramModeToggleFocusAfterRender) {
      return;
    }
    this.restoreDiagramModeToggleFocusAfterRender = false;

    setTimeout(() => {
      const toggle = this.diagramModeToggle?.nativeElement;
      if (
        !toggle ||
        toggle.disabled ||
        this.isBusy() ||
        this.renderErrorMessage()
      ) {
        return;
      }

      const activeElement = toggle.ownerDocument.activeElement;
      if (
        activeElement &&
        activeElement !== toggle &&
        activeElement !== toggle.ownerDocument.body &&
        activeElement !== toggle.ownerDocument.documentElement
      ) {
        return;
      }

      toggle.focus();
    });
  }

  private queueRender(graph: WorkflowReadOnlyGraph | null): void {
    const profile = this.renderProfile();
    const structuralKey = graph ? JSON.stringify([buildStructuralKey(graph, profile),this.layoutKeyState()]) : null;
    if (structuralKey === this.renderRequest()?.structuralKey) {
      return;
    }

    this.renderRequest.set(
      graph && structuralKey ? { graph, profile, structuralKey } : null,
    );
  }
  private savedPositions(graph:WorkflowReadOnlyGraph):WorkflowMapPositions {
    const ids=graph.nodes.filter(n=>n.sourceIndex!==null).map(n=>n.id);
    if(!this.positionsLoaded){this.positions=readWorkflowMapPositions(this.storage(),this.layoutKeyState(),ids);this.positionsLoaded=true;}
    return Object.fromEntries(Object.entries(this.positions).filter(([id])=>ids.includes(id)));
  }

  private connectResizeObserver(container: HTMLElement | undefined): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.lastCameraFitContainerSize = container
      ? containerSize(container)
      : null;

    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      const nextSize = observedContainerSize(container, entries);
      if (this.viewport) {
        if (this.applyPendingPresentationRestore()) return;
        this.visualization.resize(this.viewport);
        if (
          isMeaningfulCameraResize(this.lastCameraFitContainerSize, nextSize)
        ) {
          this.visualization.fit(this.viewport);
          this.lastCameraFitContainerSize = nextSize;
        } else if (!this.lastCameraFitContainerSize) {
          this.lastCameraFitContainerSize = nextSize;
        }
      } else {
        // The next render performs its own fit against these dimensions.
        this.lastCameraFitContainerSize = nextSize;
        this.containerMeasurementVersion.update((version) => version + 1);
      }
    });
    this.resizeObserver.observe(container);
  }

  private connectThemeClassObserver(): void {
    this.document.addEventListener('load', this.onThemeStylesheetLoad, true);
    const root = this.document.documentElement;
    const MutationObserverConstructor =
      this.document.defaultView?.MutationObserver;
    this.observedDarkMode = root.classList.contains('dark-mode');
    if (!MutationObserverConstructor) {
      return;
    }

    this.themeClassObserver = new MutationObserverConstructor(() => {
      const isDarkMode = root.classList.contains('dark-mode');
      if (isDarkMode === this.observedDarkMode) {
        return;
      }

      this.observedDarkMode = isDarkMode;
      this.applyCurrentTheme();
    });
    this.themeClassObserver.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  private applyCurrentTheme(): void {
    this.visualization.applyTheme(
      this.viewport,
      this.graphContainerRef()?.nativeElement,
    );
  }

  private recordCurrentContainerAsFitted(
    container = this.graphContainerRef()?.nativeElement,
  ): void {
    if (container) {
      this.lastCameraFitContainerSize = containerSize(container);
    }
  }

  private focusRenderError(): void {
    setTimeout(() => this.renderError?.nativeElement.focus());
  }
}

function validPresentationScroll(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 10_000_000;
}

function findElement(
  graph: WorkflowReadOnlyGraph | null,
  elementId: string | null,
): WorkflowGraphSelectableElement | null {
  if (!graph || !elementId) {
    return null;
  }

  return (
    graph.nodes.find((node) => node.id === elementId) ??
    graph.edges.find((edge) => edge.id === elementId) ??
    null
  );
}

function findDecisionGate(
  graph: WorkflowReadOnlyGraph,
  gateId: string,
): WorkflowReadOnlyGraphDecisionGate | null {
  return (
    graph.workflowDecisionGates?.find((gate) => gate.id === gateId) ??
    graph.nodes
      .flatMap((node) => node.decisionGates ?? [])
      .find((gate) => gate.id === gateId) ??
    null
  );
}

function searchableDecisionGateText(
  gate: WorkflowReadOnlyGraphDecisionGate,
): string {
  return [
    gate.title,
    gate.ownerLabel,
    gate.scopeLabel,
    gate.accessibleDescription,
  ]
    .join(' ')
    .toLocaleLowerCase();
}

function searchableElementText(item: WorkflowGraphSelectableElement): string {
  return [searchableElementCoreText(item), decisionGateAccessibleSummary(item)]
    .join(' ')
    .toLocaleLowerCase();
}

function searchableElementCoreText(
  item: WorkflowGraphSelectableElement,
): string {
  return [
    item.groupLabel,
    item.label,
    item.description,
    item.sourceId,
    ...item.detail.facts.flatMap((fact) => [fact.label, fact.value]),
  ]
    .join(' ')
    .toLocaleLowerCase();
}

function decisionGateAccessibleSummary(
  item: WorkflowGraphSelectableElement,
): string {
  if (!('decisionGates' in item)) {
    return '';
  }

  return (item.decisionGates ?? [])
    .map((gate) => gate.accessibleDescription)
    .join(' ');
}

function decisionGateCount(item: WorkflowGraphSelectableElement): number {
  return 'decisionGates' in item ? (item.decisionGates?.length ?? 0) : 0;
}

function decisionGateVisibleSummary(
  item: WorkflowGraphSelectableElement,
): string {
  const count = decisionGateCount(item);
  if (count === 0 || !('decisionGates' in item)) {
    return '';
  }

  const invalidCount = (item.decisionGates ?? []).filter(
    (gate) => gate.isInvalid,
  ).length;
  return 'Decision gates' + (invalidCount > 0 ? '; needs attention' : '');
}

function structuralDecisionGate(
  gate: WorkflowReadOnlyGraphDecisionGate,
): object {
  return {
    id: gate.id,
    title: gate.title,
    effect: gate.effect,
    accessibleDescription: gate.accessibleDescription,
    ownerSourceId: gate.ownerSourceId,
    ownerSourceIndex: gate.ownerSourceIndex,
    umlQuestion: gate.umlQuestion,
    matchedOutcomeLabel: gate.matchedOutcomeLabel,
    matchedEffect: gate.matchedEffect,
    blockedFallback: gate.blockedFallback,
    scopeKind: gate.scopeKind,
    scopeLabel: gate.scopeLabel,
    responsibilityLaneId: gate.responsibilityLaneId,
    responsibilityLaneKind: gate.responsibilityLaneKind,
    queueGuidanceCodes: gate.queueGuidanceCodes,
    suggestedRoleCodes: gate.suggestedRoleCodes,
    notificationCount: gate.notificationCount,
    isLegacyOnly: gate.isLegacyOnly,
    authoredOrigins: gate.authoredOrigins,
    decisionKindCode: gate.decisionKindCode,
    requiredOutcomeCode: gate.requiredOutcomeCode,
    reactionCode: gate.reactionCode,
    targetStateCode: gate.targetStateCode,
    appliesToEveryStatusChange: gate.appliesToEveryStatusChange,
    affectedTransitionCount: gate.affectedTransitionCount,
    authoredOriginCount: gate.authoredOriginCount,
    malformedOriginCount: gate.malformedOriginCount,
    isInvalid: gate.isInvalid,
  };
}

function hasUnambiguousSourceId(
  item: WorkflowGraphSelectableElement,
  candidates: readonly WorkflowGraphSelectableElement[],
): boolean {
  return (
    !item.isInvalid &&
    item.sourceId.trim().length > 0 &&
    candidates.filter((candidate) => candidate.sourceId === item.sourceId)
      .length === 1
  );
}

function buildStructuralKey(
  graph: WorkflowReadOnlyGraph,
  profile: WorkflowGraphLayoutProfile,
): string {
  return JSON.stringify({
    profile,
    responsibilityLanes: graph.responsibilityLanes ?? [],
    nodes: graph.nodes.map((node) => ({
      id: node.id,
      kind: node.kind,
      label: node.label,
      width: node.width,
      height: node.height,
      processOrder: node.processOrder ?? null,
      responsibilityLaneId: node.responsibilityLaneId,
      decisionGates: (node.decisionGates ?? []).map(structuralDecisionGate),
      isInvalid: node.isInvalid,
    })),
    edges: graph.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      kind: edge.kind,
      label: edge.label,
      pathKindCode:edge.pathKindCode,
      outcomeCode:edge.outcomeCode,
      isInvalid: edge.isInvalid,
    })),
    workflowDecisionGates: (graph.workflowDecisionGates ?? []).map(
      structuralDecisionGate,
    ),
    workflowDecisionGateCount: graph.workflowDecisionGateCount ?? 0,
  });
}

function hasRenderableSize(container: HTMLElement): boolean {
  const size = containerSize(container);
  return size.width > 0 && size.height > 0;
}

function containerSize(container: HTMLElement): WorkflowGraphContainerSize {
  const bounds = container.getBoundingClientRect();
  return { width: bounds.width, height: bounds.height };
}

function observedContainerSize(
  container: HTMLElement,
  entries: readonly ResizeObserverEntry[],
): WorkflowGraphContainerSize {
  const entry =
    entries.find((candidate) => candidate.target === container) ?? entries[0];
  return entry
    ? { width: entry.contentRect.width, height: entry.contentRect.height }
    : containerSize(container);
}

function isMeaningfulCameraResize(
  fittedSize: WorkflowGraphContainerSize | null,
  nextSize: WorkflowGraphContainerSize,
): boolean {
  return Boolean(
    fittedSize &&
    nextSize.width > 0 &&
    nextSize.height > 0 &&
    (Math.abs(nextSize.width - fittedSize.width) >=
      CAMERA_REFIT_RESIZE_THRESHOLD_PX ||
      Math.abs(nextSize.height - fittedSize.height) >=
        CAMERA_REFIT_RESIZE_THRESHOLD_PX),
  );
}
